import React, { useEffect, useMemo, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button } from "@mui/material";
import DashboardContainer from "../../components/dashboard-container";
import { historicalInventoryService } from "../../utils/services/historicalInventoryService";
import HistoricalInventoryList from "../../components/historical-inventory-list";
import HistoricalInventoryModal from "../../components/historical-inventory-modal";
import { historicalInventoryFilterInitialState } from "../../utils/filter-config";
import buildQuery from "../../utils/query-builder";
import ImportIcon from "../../globalStyles/icons/importIcon.js";
import CustomTypography from "../../globalStyles/fonts/index.js";

function filterReducer(state, action) {
  switch (action.type) {
    case "SET_FILTER":
      return {
        ...state,
        columns: state?.columns.map((x) =>
          x?.key === action?.payload?.key ? { ...x, ...action.payload } : x,
        ),
      };
    case "EXISTING_DATA":
      return {
        ...state,
        columns: state.columns.map((x) => ({
          ...x,
          filter:
            action.data.filters.find((y) => y.key === x.key)?.values ||
            x.filter,
        })),
        limit: action.data.limit || state.limit,
      };
    case "UPDATE_LIMIT":
      return { ...state, limit: action.payload };
    default:
      return state;
  }
}

const HistoricalInventory = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [dataCount, setDataCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [openModal, setOpenModal] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const client_id = localStorage.getItem("client_id");
  const isAdmin = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      if (user?.role === "admin") return true;
      const fromUser = Array.isArray(user?.roles) ? user.roles : [];
      const fromStorage = JSON.parse(localStorage.getItem("roles") || "[]");
      const roles = fromUser.length ? fromUser : fromStorage;
      return (
        Array.isArray(roles) &&
        roles.some((role) =>
          String(role?.name || role || "")
            .toLowerCase()
            .includes("admin"),
        )
      );
    } catch (_err) {
      return false;
    }
  }, []);

  const [filterData, dispatch] = useReducer(
    filterReducer,
    historicalInventoryFilterInitialState,
  );

  useEffect(() => {
    let data = localStorage.getItem("historical-inventory-filter-data");
    if (data) {
      localStorage.removeItem("historical-inventory-filter-data");
      dispatch({ type: "EXISTING_DATA", data: JSON.parse(data) });
    }
  }, []);

  const updateFilterData = () => {
    localStorage.setItem(
      "historical-inventory-filter-data",
      JSON.stringify({
        filters: filterData.columns.map((x) => ({
          key: x.key,
          values: x.filter,
        })),
        limit: filterData.limit,
      }),
    );
  };

  const handleFilterChange = (newFilter) => {
    dispatch({ type: "SET_FILTER", payload: newFilter });
    setPage(0);
  };

  const fetchData = async (signal) => {
    setLoading(true);
    try {
      const response = await historicalInventoryService.fetch(
        buildQuery(filterData, page),
        signal,
      );
      setData(response.data);
      setDataCount(response.total);
    } catch (error) {
      if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
        console.error("Fetch failed:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [page, filterData, fetchTrigger]);

  return (
    <DashboardContainer
      onChangeFilter={handleFilterChange}
      filterData={filterData}
      fields={filterData.columns}
      hideFilterClient={true}
      hideUploadButton
      header={"Historical Inventory"}
      setViewMode={setViewMode}
      onClickAddButton={() => setOpenModal(true)}
      title={"+ Historical Inventory"}
      onClickRefresh={() => setFetchTrigger((x) => x + 1)}
      headerButtons={
        <Box display="flex" alignItems="center" gap={1}>
          {isAdmin ? (
            <Button
              size="small"
              style={{ textTransform: "none" }}
              variant="outlined"
              onClick={() => navigate("/historical-inventory-snapshot-health")}
            >
              <CustomTypography fontSize="14px" fontWeight={500}>
                Snapshot Health
              </CustomTypography>
            </Button>
          ) : null}
          <Button
            size="small"
            style={{ textTransform: "none" }}
            variant="contained"
            onClick={() => {
              updateFilterData();
              navigate("/historical-inventory-import");
            }}
          >
            <ImportIcon fill="#FFF" />
            <CustomTypography fontSize="14px" fontWeight={500} color="#FFFFFF">
              Import
            </CustomTypography>
          </Button>
        </Box>
      }
    >
      <HistoricalInventoryModal
        open={openModal}
        selectedInventory={selectedInventory}
        handleClose={() => {
          setOpenModal(false);
          setSelectedInventory(null);
        }}
        onAddData={() => setFetchTrigger((x) => x + 1)}
      />

      <HistoricalInventoryList
        viewMode={viewMode}
        data={data}
        dataCount={dataCount}
        page={page}
        rowsPerPage={filterData.limit}
        setPage={setPage}
        setRowsPerPage={(e) => dispatch({ type: "UPDATE_LIMIT", payload: e })}
        filterData={filterData}
        onChangeFilter={handleFilterChange}
        loading={loading}
        fetchData={() => setFetchTrigger((x) => x + 1)}
        onEdit={(item) => {
          setSelectedInventory(item);
          setOpenModal(true);
        }}
        client_id={client_id}
      />
    </DashboardContainer>
  );
};

export default HistoricalInventory;
